// export default ModalDialogMotionEditForm
import { useState, useEffect, useContext, Children, Fragment } from 'react'
import GlobalContext from '../../../context/global-context'

// assets
import isologo_SII from '../../../assets/isologo_SII.png'
import LoaderMain from '../../../assets/svgIcons/loaderMain.json'

// api
import { OrganizationAPI } from '../../../api/organization.api'
import { CentroCostosAPI } from '../../../api/centroCostos.api'

// components
import { Button } from '../ui/index'
import Modal from './ModalDialogAddProps'
import { formatDate, formatCurrency, formatRut } from '../../utils/formatter'
import ModalAdmVinculacion from './ModalAdmVinculacion'
import FilterDateControl from '../table/FilterDateControl'
import dayjs from 'dayjs'
import Lottie from 'lottie-react'
import { Box } from '@mui/material'
import ResizableModal from '../ui/ResizableModal'
import {
  activosFijos,
  activosIntangibles,
  existencias,
  getExpenseTypeKeyFromAccountCode
} from '../data/dataProveedor'

// ui
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import PlanRestrictedButton from '../ui/PlanRestrictedButton'

// Colores para cada centro de costo (hasta 10)
const CENTER_COLORS = [
  '#00C6B3',
  '#FF6B6B',
  '#4F46E5',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#A855F7',
  '#F97316',
  '#22C55E'
]

const EXPENSE_TYPE_OPTIONS = [
  { id: 'Gastos', nombreCuenta: 'Gastos' },
  { id: 'Existencias', nombreCuenta: 'Existencias' },
  { id: 'Activos Fijos', nombreCuenta: 'Activos Fijos' },
  { id: 'Activos Intangibles', nombreCuenta: 'Activos Intangibles' }
]

const EXPENSE_TYPE_KEY_BY_LABEL = {
  Gastos: 'gastos',
  Existencias: 'existensias',
  'Activos Fijos': 'activos-fijos',
  'Activos Intangibles': 'activos-intangibles'
}

const EXPENSE_TYPE_LABEL_BY_KEY = {
  gastos: 'Gastos',
  existensias: 'Existencias',
  'activos-fijos': 'Activos Fijos',
  'activos-intangibles': 'Activos Intangibles'
}

/**
 * Helper para armar el payload del endpoint createLibroCentroCosto
 * El back espera directamente un ARRAY:
 * [
 *   { idCentroCosto: number; porcentajeCentroCosto: number; },
 *   ...
 * ]
 */
const buildCentroCostoPayload = rows => {
  return rows
    .filter(row => row.centroCostoId && row.porcentaje > 0)
    .map(row => ({
      idCentroCosto: Number(row.centroCostoId),
      porcentajeCentroCosto: row.porcentaje
      // idArea / porcentajeArea opcionales si algún día los usas:
      // idArea: row.idArea ? Number(row.idArea) : null,
      // porcentajeArea: row.porcentajeArea ?? null,
    }))
}

/** Círculo de progreso con segmentos por centro de costo */
const CircleProgress = ({ rows }) => {
  const total = Math.min(
    100,
    rows.reduce((acc, r) => acc + (Number(r.porcentaje) || 0), 0)
  )

  let offset = 0
  const stops = []

  rows.forEach(row => {
    const pct = Math.max(0, Math.min(100 - offset, Number(row.porcentaje) || 0))
    if (pct <= 0) return
    const color = row.color || '#00C6B3'
    stops.push(`${color} ${offset}% ${offset + pct}%`)
    offset += pct
  })

  if (offset < 100) {
    stops.push(`#E5E7EB ${offset}% 100%`)
  }

  const background = `conic-gradient(${stops.join(', ')})`

  return (
    <div className='flex flex-col items-center mt-3'>
      <div
        className='w-20 h-20 rounded-full flex items-center justify-center'
        style={{ background }}
      >
        <span className='text-xs font-semibold'>{total}%</span>
      </div>
      <span className='mt-1 text-[11px] text-gray-500 text-center font-sans'>
        Distribución % centros de costo
      </span>
    </div>
  )
}

const isForbiddenCentroName = name =>
  name?.trim().toLowerCase() === 'estado de resultados consolidado'.toLowerCase()

const toNumericId = value => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const getComparableCuentaId = value => {
  if (value === null || value === undefined || value === '') return null
  return toNumericId(value) ?? String(value)
}

const getCuentaOptionId = cuenta =>
  getComparableCuentaId(cuenta?.id_key) ??
  getComparableCuentaId(cuenta?.id) ??
  cuenta?.id_key ??
  cuenta?.id ??
  null

const getExpenseTypeKeyFromAccountId = accountId => {
  const normalizedAccountId = toNumericId(accountId)

  if (normalizedAccountId == null) return 'gastos'
  if (existencias.includes(normalizedAccountId)) return 'existensias'
  if (activosFijos.includes(normalizedAccountId)) return 'activos-fijos'
  if (activosIntangibles.includes(normalizedAccountId)) return 'activos-intangibles'

  return 'gastos'
}

const normalizeCuentaName = value =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const normalizeCuentaCode = value =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, '')

const getCuentaOptionLabel = cuenta =>
  cuenta?.nameModificado ?? cuenta?.name ?? cuenta?.nombreCuenta ?? cuenta?.nameCuenta ?? ''

const doesCuentaMatchLibro = (cuenta, libroData) => {
  const libroIds = [
    libroData?.idCuenta,
    libroData?.cuentaContableId,
    libroData?.cuentaContable?.id,
    libroData?.cuentaContable?.id_key
  ]
    .map(getComparableCuentaId)
    .filter(value => value != null)
    .map(String)
  const cuentaIds = [cuenta?.id_key, cuenta?.idKey, cuenta?.id, cuenta?.cuentaContableId]
    .map(getComparableCuentaId)
    .filter(value => value != null)
    .map(String)

  if (cuentaIds.some(id => libroIds.includes(id))) return true

  const libroCodes = [libroData?.codigoCuenta, libroData?.cuentaContable?.codigoCuenta]
    .map(normalizeCuentaCode)
    .filter(Boolean)
  const cuentaCodes = [
    cuenta?.codigo,
    cuenta?.codigoFinal,
    cuenta?.codigoCuenta,
    cuenta?.code,
    cuenta?.codeCuenta
  ]
    .map(normalizeCuentaCode)
    .filter(Boolean)

  if (cuentaCodes.some(code => libroCodes.includes(code))) return true

  const libroNames = [libroData?.nombreCuenta, libroData?.cuentaContable?.nombreCuenta]
    .map(normalizeCuentaName)
    .filter(Boolean)
  const cuentaNames = [
    cuenta?.nameModificado,
    cuenta?.name,
    cuenta?.nombreCuenta,
    cuenta?.nameCuenta
  ]
    .map(normalizeCuentaName)
    .filter(Boolean)

  return cuentaNames.some(name => libroNames.includes(name))
}

const CustomSelect = ({ label, name, options, value, onChange, disabled }) => (
  <div className='relative flex flex-col items-start'>
    {label && (
      <label
        htmlFor={name}
        className='absolute top-[-15px] left-4 z-10 bg-transparent text-xs font-sans font-medium text-gray-500'
      >
        {label}
      </label>
    )}
    <select
      className='h-9 w-full appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs font-sans'
      name={name}
      onChange={onChange}
      value={value}
      disabled={disabled}
    >
      <option value='' disabled hidden>
        Seleccione una opción
      </option>
      {options.map(option => (
        <option key={option.id} value={option.id} className='font-sans'>
          {option.nombreCuenta}
        </option>
      ))}
    </select>
  </div>
)

const AlignedControls = ({ columns = 1, children, className = '' }) => {
  const items = Children.toArray(children).filter(Boolean)
  const columnsClass = columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'

  return (
    <div className={`grid gap-4 items-end ${columnsClass} ${className}`}>
      {items.map((child, index) => (
        <div key={index} className='min-w-0 flex flex-col justify-end min-h-[52px]'>
          {child}
        </div>
      ))}
    </div>
  )
}

const ModalDialogMotionEditForm = ({
  open,
  setOpen,
  idLibro,
  type,
  status,
  setSwitchGetData,
  title,
  aprobado,
  hideMovementSummary = false,
  contextLabel = '',
  modalSize = 'default',
  modalWidth = 1200,
  modalHeight = 680,
  bulkCentroCostoLibroIds,
  bulkCentroCostoReemplazar = true,
  movementSummary
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [data, setData] = useState({
    validation: ''
  })
  const { ui, currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()
  const centroCostosAPI = new CentroCostosAPI()
  const [, setSwitchDate] = useState(false)
  const [selectedCuentaVenta, setSelectedCuentaVenta] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [selectedType, setSelectedType] = useState('Gastos')
  const [selectedClasificador, setSelectedClasificador] = useState(null)
  const [dataContable, setDataContable] = useState([])
  const [openModalCreate, setOpenModalCreate] = useState(false)
  const [reloadData] = useState(false)
  const [, setReload] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasInitializedCentroFromData, setHasInitializedCentroFromData] = useState(false)
  const [centroCostoError, setCentroCostoError] = useState('')
  const [clasificacionTemporalidad, setClasificacionTemporalidad] = useState(1)
  const [centroCostoTemporalidad, setCentroCostoTemporalidad] = useState(0)
  const [initialCentroTemporalidad, setInitialCentroTemporalidad] = useState(0)
  const [forceBulkCentroCostoSubmit, setForceBulkCentroCostoSubmit] = useState(false)

  // helper para crear filas iniciales con color
  const buildInitialRows = (count = 2) =>
    Array.from({ length: count }).map((_, i) => ({
      centroCostoId: '',
      porcentaje: 0,
      color: CENTER_COLORS[i % CENTER_COLORS.length]
    }))

  const getAvailableCentroCostosCount = () => {
    if (!Array.isArray(centroCostos)) return 0

    return centroCostos.filter(cc => !isForbiddenCentroName(cc.name)).length
  }
  // Centros de costo + porcentajes (2 filas por defecto, con color)
  const [centrosCostoRows, setCentrosCostoRows] = useState(buildInitialRows(2))

  // Centros de costo traídos desde el back
  const [centroCostos, setCentroCostos] = useState([])

  // Estados para guardar el “estado inicial” de los payloads
  const [initialLibroPayload, setInitialLibroPayload] = useState(null)
  const [initialCentroCostoPayload, setInitialCentroCostoPayload] = useState(null)
  const [initializedLibroPayload, setInitializedLibroPayload] = useState(false)
  const isBulkCentroCostoMode = Array.isArray(bulkCentroCostoLibroIds)
  const isAnulado = String(status || movementSummary?.status || '')
    .toUpperCase()
    .includes('ANUL')

  const handleClose = () => {
    setOpen(false)
  }

  const functionGetDataLibroContable = async (overrideType = null) => {
    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(currentBusiness.id, idLibro, type)
      const libroData = response?.data ?? {}
      let selectedTypeKey = 'ingreso'
      let cuentas = []

      if (type !== 'venta') {
        if (overrideType) {
          selectedTypeKey = EXPENSE_TYPE_KEY_BY_LABEL[overrideType] || 'gastos'
          setSelectedType(overrideType)
          const responseCuentas = await organizationAPI.getCuentasContables({
            businessId: currentBusiness.id,
            type: selectedTypeKey
          })
          cuentas = responseCuentas?.data ?? []
        } else {
          const fallbackTypeKey = getExpenseTypeKeyFromAccountId(libroData?.idCuenta)
          const codeTypeKey = getExpenseTypeKeyFromAccountCode(
            libroData?.codigoCuenta ?? libroData?.cuentaContable?.codigoCuenta
          )
          const typeKeys = Object.keys(EXPENSE_TYPE_LABEL_BY_KEY)
          const responses = await Promise.allSettled(
            typeKeys.map(accountType =>
              organizationAPI.getCuentasContables({
                businessId: currentBusiness.id,
                type: accountType
              })
            )
          )
          const catalogs = typeKeys.map((accountType, index) => ({
            accountType,
            cuentas:
              responses[index]?.status === 'fulfilled' &&
              Array.isArray(responses[index].value?.data)
                ? responses[index].value.data
                : []
          }))
          const matchingCatalog = catalogs.find(catalog =>
            catalog.cuentas.some(cuenta => doesCuentaMatchLibro(cuenta, libroData))
          )
          selectedTypeKey = codeTypeKey ?? matchingCatalog?.accountType ?? fallbackTypeKey
          cuentas =
            catalogs.find(catalog => catalog.accountType === selectedTypeKey)?.cuentas ?? []
          setSelectedType(EXPENSE_TYPE_LABEL_BY_KEY[selectedTypeKey] || 'Gastos')
        }
      } else {
        const responseCuentas = await organizationAPI.getCuentasContables({
          businessId: currentBusiness.id,
          type: selectedTypeKey
        })
        cuentas = responseCuentas?.data ?? []
      }
      setSwitchDate(false)
      setData(libroData)
      setDataContable(cuentas)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Carga centros de costo para las opciones
  const getCentroCostos = async () => {
    if (!currentBusiness?.id) {
      setCentroCostos([])
      return
    }

    try {
      const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
      setCentroCostos(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Error al cargar los centros de costo:', error)
      setCentroCostos([])
    }
  }

  useEffect(() => {
    if (!open) return
    getCentroCostos()
  }, [open, currentBusiness?.id])

  /**
   * Submit:
   * - Detecta cambios en clasificación/libro (patchDataLibroContable)
   * - Detecta cambios en centros de costo (createLibroCentroCosto)
   * - Llama a uno, al otro o a ambos según corresponda
   */
  const handleSubmit = async () => {
    try {
      ui.setLoader({ visible: true })
      // limpiamos cualquier error previo
      setCentroCostoError('')

      // 1) Payload del patch (clasificación / temporalidad / glosa)
      const cuentaContableId = type === 'venta' ? selectedCuentaVenta : selectedClasificador
      const useBulkCentroCosto = isBulkCentroCostoMode
      const canUpdateLibro = data.validation !== -5 && !isAnulado

      const libroPayload = canUpdateLibro
        ? {
            cuentaContableId: getComparableCuentaId(cuentaContableId),
            glosa: data.glosa || '',
            vencimiento: new Date(data.fechaVencimiento).toISOString(),
            validation: clasificacionTemporalidad
          }
        : null

      // 2) Payload de centros de costo (ARRAY)
      const centroCostoPayload = buildCentroCostoPayload(centrosCostoRows)
      const temporalidad = isAnulado ? 0 : Number(centroCostoTemporalidad ?? 0) || 0
      const normalizedBulkCentroCostoIds = useBulkCentroCosto
        ? [...new Set(bulkCentroCostoLibroIds.map(Number).filter(Number.isFinite))]
        : []

      // 3) Detectar cambios en el payload del libro
      const hasLibroChanges = !canUpdateLibro
        ? false
        : initialLibroPayload
          ? JSON.stringify(libroPayload) !== JSON.stringify(initialLibroPayload)
          : Boolean(cuentaContableId)

      // 4) Detectar cambios en el payload de centros de costo
      const hasCentroCostoPayloadChanges = initialCentroCostoPayload
        ? JSON.stringify(centroCostoPayload) !== JSON.stringify(initialCentroCostoPayload)
        : centroCostoPayload.length > 0
      const hasSingleCentroCostoChanges = initialCentroCostoPayload
        ? hasCentroCostoPayloadChanges || temporalidad !== initialCentroTemporalidad
        : centroCostoPayload.length > 0
      const shouldSubmitBulkCentroCosto =
        useBulkCentroCosto &&
        normalizedBulkCentroCostoIds.length > 0 &&
        (centroCostoPayload.length > 0 ||
          hasCentroCostoPayloadChanges ||
          forceBulkCentroCostoSubmit)
      const hasCentroCostoChanges = useBulkCentroCosto
        ? shouldSubmitBulkCentroCosto
        : hasSingleCentroCostoChanges

      // 5) SOLO si hubo cambios en centros de costo Y hay algo en el payload,
      //    validamos suma = 100% y que no se repitan IDs.
      if (hasCentroCostoChanges && centroCostoPayload.length > 0) {
        const totalPorcentaje = centroCostoPayload.reduce(
          (acc, item) => acc + (item.porcentajeCentroCosto || 0),
          0
        )

        if (totalPorcentaje !== 100) {
          setCentroCostoError('La suma de los porcentajes de centros de costo debe sumar 100%.')
          return
        }

        const ids = centroCostoPayload.map(item => item.idCentroCosto)
        const idsSet = new Set(ids)

        if (idsSet.size !== ids.length) {
          setCentroCostoError('No puedes usar el mismo centro de costo en más de una fila.')
          return
        }
      }

      const promises = []

      if (hasLibroChanges) {
        promises.push(
          organizationAPI.patchDataLibroContable(currentBusiness?.id, idLibro, libroPayload)
        )
      }

      if (hasCentroCostoChanges && useBulkCentroCosto) {
        promises.push(
          centroCostosAPI.createLibrosCentroCosto(
            currentBusiness?.id,
            {
              lista: centroCostoPayload,
              ids: normalizedBulkCentroCostoIds
            },
            bulkCentroCostoReemplazar
          )
        )
      } else if (hasCentroCostoChanges) {
        promises.push(
          centroCostosAPI.createLibroCentroCosto(
            currentBusiness?.id,
            idLibro,
            centroCostoPayload,
            temporalidad
          )
        )
      }

      // Si no hay nada que enviar, sólo cerramos
      if (promises.length === 0) {
        handleClose()
        return
      }

      await Promise.all(promises)

      setSwitchGetData(true)
      functionGetDataLibroContable()
      handleClose()
    } catch (error) {
      console.error(error)
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  // Cada vez que se abre el modal reseteamos las referencias iniciales
  useEffect(() => {
    if (open) {
      setInitialLibroPayload(null)
      setInitialCentroCostoPayload(null)
      setInitialCentroTemporalidad(0)
      setInitializedLibroPayload(false)
      setHasInitializedCentroFromData(false)
      setInitialized(false)
      setSelectedCuentaVenta(null)
      setSelectedClasificador(null)
      setSelectedType('Gastos')
      setForceBulkCentroCostoSubmit(false)

      functionGetDataLibroContable()
      setData(prevData => ({
        ...prevData,
        validation: prevData.validation ?? ''
      }))
    }
  }, [reloadData, open])

  useEffect(() => {
    if (!open) return

    // 1) Aún no se ha cargado la data del back → no hacer nada
    if (typeof data.centro === 'undefined') {
      return
    }

    // 2) Ya inicializamos una vez con la data actual
    if (hasInitializedCentroFromData) return

    const hasCentros = Array.isArray(data.centro) && data.centro.length > 0

    if (hasCentros) {
      // Creamos filas a partir de data.centro
      const rowsFromData = data.centro.map((item, index) => ({
        centroCostoId: String(item.centroCostoId), // value = centroCostoId
        porcentaje: item.porcentajeCentroCosto ?? 0,
        color: CENTER_COLORS[index % CENTER_COLORS.length]
      }))

      setCentrosCostoRows(rowsFromData)
      setInitialCentroCostoPayload(buildCentroCostoPayload(rowsFromData))
    } else {
      // Sin centros asociados → comportamiento anterior: 2 filas vacías
      const emptyRows = buildInitialRows(2)
      setCentrosCostoRows(emptyRows)
      setInitialCentroCostoPayload(buildCentroCostoPayload(emptyRows))
    }

    setInitialCentroTemporalidad(Number(centroCostoTemporalidad ?? 0) || 0)
    setHasInitializedCentroFromData(true)
  }, [open, data.centro, hasInitializedCentroFromData, centroCostoTemporalidad])

  // Manejo filas de centros de costo
  const handleCentroCostoRowChange = (index, field, value) => {
    setCentrosCostoRows(prev => {
      const clone = [...prev]

      if (field === 'porcentaje') {
        // Permitimos string vacío mientras escribe
        const parsed = value === '' ? '' : Math.max(0, Math.min(100, Number(value) || 0))

        clone[index] = {
          ...clone[index],
          porcentaje: parsed
        }
      } else {
        clone[index] = {
          ...clone[index],
          [field]: value
        }
      }
      return clone
    })
    setCentroCostoError('')
  }

  const addCentroCostoRow = () => {
    setCentrosCostoRows(prev => {
      const maxRows = getAvailableCentroCostosCount() // 👈 número de centros de costo reales

      // Si ya tengo tantas filas como centros de costo disponibles, no agrego más
      if (maxRows > 0 && prev.length >= maxRows) {
        return prev
      }

      const nextIndex = prev.length
      const newRow = {
        centroCostoId: '',
        porcentaje: 0,
        color: CENTER_COLORS[nextIndex % CENTER_COLORS.length]
      }
      return [...prev, newRow]
    })
    setCentroCostoError('')
  }

  const removeCentroCostoRow = index => {
    setCentrosCostoRows(prev => {
      return prev.filter((_, i) => i !== index)
    })
    setCentroCostoError('')
  }

  const clearCentroCostoRows = () => {
    setForceBulkCentroCostoSubmit(true)
    setCentrosCostoRows([])
    setCentroCostoError('')
  }

  useEffect(() => {
    if (data.linkage) {
      setReload(prev => !prev)
    }
  }, [data])

  useEffect(() => {
    if (data && data.validation === undefined && aprobado != null && type === 'venta') {
      setData(prevData => ({
        ...prevData,
        validation: aprobado
      }))
    }
  }, [data, aprobado, type])

  useEffect(() => {
    if (data && data.nombreCuenta && !initialized) {
      const cuentaSeleccionada =
        dataContable.find(cuenta => doesCuentaMatchLibro(cuenta, data))
      const cuentaSeleccionadaId = getCuentaOptionId(cuentaSeleccionada)

      if (cuentaSeleccionadaId != null) {
        if (type === 'venta') {
          setSelectedCuentaVenta(cuentaSeleccionadaId)
        } else {
          setSelectedClasificador(cuentaSeleccionadaId)
        }
      }
      setInitialized(true)
    }
  }, [data, dataContable, type, initialized])

  // Snapshot inicial del payload de libro
  useEffect(() => {
    if (initializedLibroPayload) return
    if (!data || !data.fechaVencimiento) return

    const cuentaContableId = type === 'venta' ? selectedCuentaVenta : selectedClasificador
    if (!cuentaContableId) return

    setInitialLibroPayload({
      cuentaContableId: getComparableCuentaId(cuentaContableId),
      glosa: data.glosa || '',
      vencimiento: new Date(data.fechaVencimiento).toISOString(),
      validation:
        data.validation !== undefined && data.validation !== null
          ? data.validation
          : clasificacionTemporalidad
    })
    setInitializedLibroPayload(true)
  }, [
    data,
    selectedCuentaVenta,
    selectedClasificador,
    type,
    initializedLibroPayload,
    clasificacionTemporalidad
  ])

  useEffect(() => {
    if (!open) return
    setClasificacionTemporalidad(Number(data?.validation ?? 1))
    setCentroCostoTemporalidad(0)
    setInitialCentroTemporalidad(0)
  }, [open, data.validation])

  const handleOptionSelect = e => {
    if (e?.target) {
      const { name, value } = e.target

      setData(prevData => ({
        ...prevData,
        [name]: name === 'validation' ? Number(value) : value
      }))

        switch (name) {
        case 'idCuentaVenta':
          setSelectedCuentaVenta(getComparableCuentaId(value))
          break
        case 'idCuentaClasificador':
          setSelectedClasificador(getComparableCuentaId(value))
          break
        default:
          break
      }
    } else {
      const newDate = dayjs(e)
      const formattedDate = newDate.format('YYYY-MM-DD')
      setData(prevData => ({
        ...prevData,
        fechaVencimiento: formattedDate
      }))
    }
  }

  const handleTypeSelect = e => {
    const newType = e.target.value
    setSelectedType(newType)
    setSelectedClasificador(null)
    setInitialized(false)
    functionGetDataLibroContable(newType)
  }

  const handleClasificadorSelect = e => {
    setSelectedClasificador(getComparableCuentaId(e.target.value))
  }

  // Opciones para CUENTAS CONTABLES (clasificación)
  const getFilteredOptions = () => {
    return dataContable.map(item => ({
      id: getCuentaOptionId(item),
      nombreCuenta: getCuentaOptionLabel(item)
    }))
  }

  const getCentroCostoOptionsForRow = index => {
    // Centros de costo que vienen de la API de la empresa
    // Filtramos el que no se debe mostrar
    if (!Array.isArray(centroCostos)) return []

    const fromApi = centroCostos
      .filter(cc => !isForbiddenCentroName(cc.name)) // 👈 nunca mostrar este
      .map(cc => ({
        id: cc.id,
        nombreCuenta: cc.name
      }))

    // La cantidad de opciones será EXACTAMENTE la cantidad de centros de costos
    // que llegan desde la API (menos el "Estado de Resultados Consolidado").
    return fromApi
  }

  const maxCentroRows = getAvailableCentroCostosCount()
  const clasificacionOptions = getFilteredOptions()
  const isLibroFieldsDisabled = data.validation === -5 || isAnulado

  const leftSideRows = [
    <div key='left-title'>
      <h2 className='text-sm font-sans m-0'>Ajustes de Clasificación</h2>
      {isBulkCentroCostoMode ? (
        <p className='text-xs text-gray-400 font-sans m-0 mt-1'>
        </p>
      ) : null}
    </div>,
    <AlignedControls key='left-tipo' columns={1}>
      {type === 'venta' ? (
        <CustomSelect
          label='Tipo'
          name='idCuentaVenta'
          options={clasificacionOptions}
          value={selectedCuentaVenta || ''}
          onChange={handleOptionSelect}
          disabled={isLibroFieldsDisabled || clasificacionOptions.length === 0}
        />
      ) : (
        <CustomSelect
          label='Tipo'
          name='selectedType'
          options={EXPENSE_TYPE_OPTIONS}
          value={selectedType}
          onChange={handleTypeSelect}
          disabled={isLibroFieldsDisabled}
        />
      )}
    </AlignedControls>,
    type !== 'venta' ? (
      <AlignedControls key='left-clasificacion' columns={1}>
        <CustomSelect
          label='Clasificación'
          name='idCuentaClasificador'
          options={clasificacionOptions}
          value={selectedClasificador || ''}
          onChange={handleClasificadorSelect}
          disabled={isLibroFieldsDisabled}
        />
      </AlignedControls>
    ) : null,
    <AlignedControls key='left-temporalidad' columns={1}>
      <div className='relative flex flex-col items-start'>
        <label className='absolute top-[-15px] left-4 z-10 bg-transparent text-xs font-medium text-gray-500 font-sans'>
          Temporalidad clasificación
        </label>
        <select
          className='h-9 appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs w-full pl-4'
          name='clasificacionTemporalidad'
          value={isLibroFieldsDisabled ? '' : clasificacionTemporalidad}
          onChange={e => setClasificacionTemporalidad(Number(e.target.value))}
          disabled={isLibroFieldsDisabled}
        >
          <option value='' disabled hidden>
            Seleccione temporalidad
          </option>
          <option value={1}>Aplicar sólo a este registro</option>
          <option value={2}>Recordar esta selección en el futuro</option>
          <option value={3}>Aplicar esta selección a todos los registros</option>
        </select>
      </div>
    </AlignedControls>,
    <AlignedControls key='left-vencimiento' columns={1}>
      <FilterDateControl
        label='Fecha Vencimiento'
        value={dayjs(data.fechaVencimiento).format('YYYY-MM-DD')}
        onChange={e => handleOptionSelect(e)}
        disabled={isLibroFieldsDisabled}
      />
    </AlignedControls>
  ].filter(Boolean)

  const rightSideRows = [
    <h2 key='right-title' className='text-sm font-sans m-0'>
      Ajustes Centro de Costos
    </h2>,
    <AlignedControls key='right-temporalidad' columns={1}>
      <PlanRestrictedButton
        keepEnabled={true}
        requiredPermission='allowCostCenter'
        message='Los centros de costos'
      >
        <div className='relative flex flex-col items-start'>
          <label className='absolute top-[-15px] left-4 z-10 bg-transparent text-xs font-medium text-gray-500 leading-tight'>
            Temporalidad centro costos
          </label>
          <select
            className='h-9 appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs w-full pl-4'
            name='centroCostoTemporalidad'
            value={isAnulado ? 0 : centroCostoTemporalidad}
            onChange={e => setCentroCostoTemporalidad(Number(e.target.value))}
            disabled={data.validation === -5 || isAnulado}
          >
            <option value={0}>Solo este movimiento</option>
            <option value={1}>Este registro y todos los anteriores</option>
            <option value={2}>Este registro y todos los posteriores</option>
            <option value={3}>Aplicar a todos los registros</option>
          </select>
        </div>
      </PlanRestrictedButton>
    </AlignedControls>,
    ...centrosCostoRows.map((row, index) => (
      <AlignedControls key={`right-cc-row-${index}`} columns={2}>
        <PlanRestrictedButton
          keepEnabled={true}
          requiredPermission='allowCostCenter'
          message='Los centros de costos'
          disabled={data.validation === -5}
        >
          <CustomSelect
            label='Centro de costo'
            name={`centroCosto-${index}`}
            options={getCentroCostoOptionsForRow(index)}
            value={row.centroCostoId}
            onChange={e => handleCentroCostoRowChange(index, 'centroCostoId', e.target.value)}
            disabled={data.validation === -5}
          />
        </PlanRestrictedButton>

        <div className='relative flex flex-col items-start'>
          <label className='absolute top-[-15px] left-4 z-10 bg-transparent text-xs font-medium text-gray-500'>
            Porcentaje
          </label>
          <div className='flex items-center gap-2 w-full'>
            <input
              type='number'
              min={0}
              max={100}
              className='h-9 w-full border border-gray-300 rounded-full px-3 py-1 text-xs focus:outline-none focus:border-gray-400'
              value={row.porcentaje === 0 ? '' : row.porcentaje}
              onFocus={() => {
                if (row.porcentaje === 0) {
                  handleCentroCostoRowChange(index, 'porcentaje', '')
                }
              }}
              onBlur={e => {
                if (e.target.value === '') {
                  handleCentroCostoRowChange(index, 'porcentaje', 0)
                }
              }}
              onChange={e => handleCentroCostoRowChange(index, 'porcentaje', e.target.value)}
              disabled={data.validation === -5}
            />

            <button
              type='button'
              onClick={() => removeCentroCostoRow(index)}
              className='text-red-500 hover:text-red-700 flex items-center justify-center'
              title='Eliminar centro de costo'
              disabled={data.validation === -5}
            >
              <ClearOutlinedIcon fontSize='small' />
            </button>
          </div>
        </div>
      </AlignedControls>
    )),
    <div key='right-actions' className='mt-1 flex w-full items-center justify-between font-sans'>
      <button
        type='button'
        className={`text-xs ${
          maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-accent3'
        }`}
        onClick={addCentroCostoRow}
        disabled={
          data.validation === -5 || (maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows)
        }
      >
        + Agregar centro de costo
      </button>
      <button
        type='button'
        className={`text-xs ${
          data.validation === -5 || centrosCostoRows.length === 0
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-red-500 hover:text-red-600'
        }`}
        onClick={clearCentroCostoRows}
        disabled={data.validation === -5 || centrosCostoRows.length === 0}
      >
        - Quitar asignación
      </button>
    </div>,
    centroCostoError ? (
      <p key='right-error' className='text-xs text-red-500 mt-1'>
        {centroCostoError}
      </p>
    ) : null,
    <CircleProgress key='right-chart' rows={centrosCostoRows} />
  ].filter(Boolean)

  const alignedRowsCount = Math.max(leftSideRows.length, rightSideRows.length)
  const summaryRut =
    movementSummary?.rut && movementSummary.rut !== 'Varios RUT'
      ? formatRut(movementSummary.rut)
      : movementSummary?.rut || '-'
  const summaryTotal = movementSummary?.montoTotal ?? data.montoTotal
  const summaryIva = movementSummary?.montoIvaRetenido ?? data.montoIva
  const summaryNeto = movementSummary?.montoNetoLiquido ?? data.montoNeto
  const summaryExento = movementSummary?.montoExento ?? data.montoExento

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      width={modalWidth}
      height={modalHeight}
      size={modalSize}
      footer={
        <>
          <Button
            onClick={handleClose}
            className='uppercase text-sm bg-primary text-white rounded-full'
            title='cerrar'
          />
          <Button
            disabled={false}
            onClick={handleSubmit}
            className='uppercase text-sm text-white rounded-full bg-accent2'
            title='guardar cambios'
          />
        </>
      }
    >
      <>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
            <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
          </Box>
        ) : (
          <>
            <div className='px-4 md:px-8 lg:px-12 mt-2 max-h-[70vh] overflow-y-auto pb-2'>
              <div className='w-full mx-auto font-sans font-medium'>
                {hideMovementSummary ? (
                  <section className='bg-white shadow-lg px-3 py-2 rounded-3xl gap-6'>
                    <section className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4'>
                      <div className='flex flex-col sm:flex-row flex-wrap items-start gap-4'>
                        <div className='flex flex-col'>
                          <div className='flex items-center gap-2'>
                            <img className='w-11 h-11 border' src={isologo_SII} alt='' />
                            <div className='flex flex-col leading-tight'>
                              <p className='text-[12px] font-bold uppercase'>
                                {movementSummary?.title || title}
                              </p>
                              {movementSummary?.count ? (
                                <p className='text-[12px] font-semibold uppercase'>
                                  {movementSummary.count} {movementSummary.countLabel || 'documentos'}
                                </p>
                              ) : null}
                              <div className='flex items-center gap-3 text-[12px] leading-tight'>
                                <span>{movementSummary?.label || contextLabel}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col items-end text-right text-[15px] min-w-[110px] whitespace-nowrap'>
                        <span className='text-accent3'>{movementSummary?.status || status}</span>
                        <p className='text-[15px] font-semibold'>
                          {formatCurrency(summaryTotal)}
                        </p>
                        <p className='text-[12px] leading-tight'>
                          IVA: {formatCurrency(summaryIva)}
                        </p>
                        {summaryNeto !== 0 && (
                          <p className='text-[12px] leading-tight'>
                            Neto: {formatCurrency(summaryNeto)}
                          </p>
                        )}
                        {summaryExento != null && summaryExento !== 0 && (
                          <p className='text-[12px] leading-tight'>
                            Exento: {formatCurrency(summaryExento)}
                          </p>
                        )}
                      </div>
                    </section>
                    <section className='flex justify-between items-center gap-2 pb-2'>
                      <div className='text-[13px] flex flex-col gap-0.5'>
                        <p className='font-bold uppercase leading-tight'>
                          {movementSummary?.razonSocial || '-'}
                        </p>
                        <p className='leading-tight'>{summaryRut}</p>
                      </div>
                    </section>
                  </section>
                ) : (
                  <section className='bg-white shadow-lg px-3 py-2 rounded-3xl gap-6'>
                    <section className='flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4'>
                      <div className='flex flex-col sm:flex-row flex-wrap items-start gap-4'>
                        <div className='flex flex-col'>
                          <div className='flex items-center gap-2'>
                            <img className='w-11 h-11 border' src={isologo_SII} alt='' />
                            <div className='flex flex-col leading-tight'>
                              <p className='text-[12px] font-bold uppercase'>
                                {type === 'honorario'
                                  ? data.nombreCuenta
                                  : data.nombreCodigoDocumento}
                                {type === 'honorario'
                                  ? data.codigoCuenta
                                  : `(${data.codigoDocumento})`}
                              </p>
                              {type !== 'honorario' && (
                                <p className='text-[12px] font-semibold uppercase'>
                                  Folio {data.folio}
                                </p>
                              )}
                              <div className='flex items-center gap-3 text-[12px] leading-tight'>
                                <span>{formatDate(data.fecha)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col items-end text-right text-[15px] min-w-[110px] whitespace-nowrap'>
                        <span className='text-accent3'>{status}</span>
                        <p className='text-[15px] font-semibold'>{formatCurrency(data.montoTotal)}</p>
                        <p className='text-[12px] leading-tight'>
                          IVA: {formatCurrency(data.montoIva)}
                        </p>
                        {type !== 'honorario' && data.montoNeto !== 0 && (
                          <p className='text-[12px] leading-tight'>
                            Neto: {formatCurrency(data.montoNeto)}
                          </p>
                        )}
                        {data.montoExento !== 0 && (
                          <p className='text-[12px] leading-tight'>
                            Exento: {formatCurrency(data.montoExento)}
                          </p>
                        )}
                      </div>
                    </section>
                    <section className='flex justify-between items-center gap-2 pb-2'>
                      <div className='text-[13px] flex flex-col gap-0.5'>
                        <p className='font-bold uppercase leading-tight'>{data.razonSocial}</p>
                        <div className='flex justify-between items-center'>
                          <p className='leading-tight'>{formatRut(data.rut)}</p>
                        </div>
                      </div>
                    </section>
                  </section>
                )}

                <section className='w-full p-4 mt-3 border-2 rounded-3xl h-auto shadow-lg'>
                  <div className='relative'>
                    <div
                      className='hidden lg:block absolute inset-y-0 left-1/2 w-px bg-gray-200'
                      aria-hidden='true'
                    />
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4 items-start'>
                      {Array.from({ length: alignedRowsCount }).map((_, index) => (
                        <Fragment key={index}>
                          <div className='min-w-0'>{leftSideRows[index] || null}</div>
                          <div className='min-w-0'>{rightSideRows[index] || null}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </>
        )}
        <Modal open={modalOpen} setOpen={setModalOpen} />
        {openModalCreate && (
          <ModalAdmVinculacion
            open={openModalCreate}
            setOpen={setOpenModalCreate}
            type={type}
            idLibro={idLibro}
            idPrincipal={data.idPrincipal}
            montoTotal={data.montoTotal}
            dataLinkage={data.linkage}
          />
        )}
      </>
    </ResizableModal>
  )
}

export default ModalDialogMotionEditForm
